import React, { useState, useEffect } from 'react';
import { FaHospital, FaHeartbeat, FaUserMd, FaBrain, FaRegCircle, FaUser, FaPhone, FaCity, FaComment, FaAmbulance, FaProcedures } from 'react-icons/fa';
import '../style.css';

const MedicalImageGallery = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const medicalImages = [
    {
      url: "https://tse1.mm.bing.net/th/id/OIP.l25PdxEDTxD2SITt4QnkowHaE4?rs=1&pid=ImgDetMain&o=7&rm=3",
      category: "Hospital",
      icon: <FaHospital />
    },
    {
      url: "https://tse3.mm.bing.net/th/id/OIP.dvGMmtfH8xqMQrhHZWENXwHaE7?pid=ImgDet&w=189&h=125&c=7&o=7&rm=3",
      category: "Hospital",
      icon: <FaHospital />
    },
    {
      url: "https://neurosurgery.wustl.edu/wp-content/uploads/2020/05/Robotic-assisted-spine-surgery-1.jpg",
      category: "Surgery",
      icon: <FaUserMd />
    },
    {
      url: "https://medical.dpu.edu.in/img/Departments/Paediatrics/new/10.jpg",
      title: "Medical Equipment",
      category: "Equipment",
      icon: <FaHeartbeat />
    },
    {
      url: "https://gregorylekovic.com/wp-content/uploads/2023/08/gl-surgery-3-1024x683.jpg",
      title: "Brain Surgery Procedure",
      category: "Neurosurgery",
      icon: <FaBrain />
    },
    {
      url: "https://www.buildingengineering.com.au/wp-content/uploads/2023/02/Bldg-Eng-Austin-Hospital-Intensive-Care-Unit-Emergency-Department-Refurbishment-08-3.jpg",
      title: "Emergency & ICU Department",
      category: "Emergency",
      icon: <FaAmbulance />
    },
    {
      url: "https://images.medindia.net/images/in-hospital/patients/hospital-patient-2.jpg",
      title: "Patient Recovery Ward",
      category: "Recovery",
      icon: <FaProcedures />
    }
  ];

  // Show only 6 images on desktop, 4 on mobile
  const imagesToShow = screenWidth <= 768 
    ? medicalImages.slice(0, 4)
    : medicalImages.slice(0, 6);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        phone: '',
        city: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <div className="med-gallery-with-form">
      {/* Header Section */}
      <div className="med-form-header">
        <div className="header-content">
          <span className="med-badge"> Medical Gallery & Enquiry</span>
          <h2 className="med-title">Our Advanced Medical Facilities</h2>
          <p className="med-subtitle">State-of-the-art hospital infrastructure and surgical equipment</p>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="two-column-layout">
        {/* Left Column - Image Gallery (Now with 6 images) */}
        <div className="left-column">
          <div className="image-grid-container">
            <div className="image-grid">
              {imagesToShow.map((image, index) => (
                <div 
                  key={index}
                  className={`grid-card ${activeIndex === index ? 'card-active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                >
                  <div className="image-container">
                    <div className="image-wrapper">
                      <img 
                        src={image.url} 
                        alt={image.title}
                        className="med-img"
                        loading="lazy"
                        style={{ objectPosition: 'center' }}
                      />
                    </div>
                    <div className="img-overlay">
                      <div className="category-tag">
                        {image.icon}
                        <span>{image.category}</span>
                      </div>
                    </div>
                  </div>
                  <div className="card-info">
                    <h3 className="card-title">{image.title}</h3>
                    <div className="img-number">{index + 1}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Image Count Info */}
            <div className="image-count-info">
              <p><strong>{imagesToShow.length}</strong> Medical Facilities Showcased</p>
            </div>

            {/* Dots Navigation */}
            <div className="dots-container">
              {imagesToShow.map((_, index) => (
                <button
                  key={index}
                  className={`dot-btn ${activeIndex === index ? 'dot-active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`View image ${index + 1}`}
                >
                  <FaRegCircle className="dot-icon" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Enquiry Form */}
        <div className="right-column">
          <div className="enquiry-form-container">
            <div className="form-header">
              <h3 className="form-title">Enquire Now</h3>
              <p className="form-subtitle">Get in touch with our medical experts</p>
            </div>
            
            {isSubmitted ? (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h3>Thank You!</h3>
                <p>Your enquiry has been submitted successfully. Our team will contact you shortly.</p>
              </div>
            ) : (
              <form className="enquiry-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <div className="input-icon">
                    <FaUser />
                  </div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <div className="input-icon">
                    <FaPhone />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <div className="input-icon">
                    <FaCity />
                  </div>
                  <input
                    type="text"
                    name="city"
                    placeholder="City"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <div className="input-icon">
                    <FaComment />
                  </div>
                  <textarea
                    name="message"
                    placeholder="Message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    required
                  />
                </div>
                
                <button type="submit" className="submit-btn">
                  Send Message
                </button>
              </form>
            )}
            
            <div className="form-footer">
              <p className="footer-text">
                <strong>Response Time:</strong> Within 24 hours
              </p>
              <p className="footer-text">
                <strong>Contact:</strong> +91 80903 11155
              </p>
              <p className="footer-text">
                <strong>Email:</strong> sandy27u@gmail.com.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalImageGallery;